import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import Modal from "./Modal";
import { ProjectAPI } from "../api/project";
import { ProjectRulesFileInfo, SpecificationFileInfo, SpecZipGroup } from "../types";
import { useNotification } from "../contexts/NotificationContext";
import { formatBackendDateTimeLocal } from "../utils/formatBackendDateTimeLocal";

const ModalContent = styled.div`
  padding: 2rem;
  max-width: 1200px;
  width: 100%;
`;

const ModalHeader = styled.div`
  margin-bottom: 2rem;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const TableHeader = styled.th`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
`;

const TableCell = styled.td`
  border: 1px solid #dee2e6;
  padding: 0.75rem;
  vertical-align: top;
`;

const SummaryButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 0.875rem;

  &:hover {
    background-color: #0056b3;
  }
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 0.5rem 0 1rem 0;
`;

const BackButton = styled.button`
  background-color: #e2e8f0;
  color: #1f2937;
  border: 1px solid #cbd5e1;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;

  &:hover {
    background-color: #cbd5e1;
  }
`;

const SummaryBody = styled.div`
  font-size: 0.95rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textPrimary};
  word-break: break-word;
  overflow-wrap: anywhere;

  pre,
  code {
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6c757d;
  font-style: italic;
`;

const DeleteIconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  color: #6c757d;
  &:hover {
    color: #dc3545;
  }
`;

interface FileInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: "projectRules" | "specDocs";
  projectId: string;
  onDataLoaded?: () => void;
  onSpecZipDeleted?: () => void;
  refreshTrigger?: number;
}

export const FileInfoModal: React.FC<FileInfoModalProps> = ({
  isOpen,
  onClose,
  type,
  projectId,
  onDataLoaded,
  onSpecZipDeleted,
  refreshTrigger,
}) => {
  const [fileData, setFileData] = useState<
    ProjectRulesFileInfo[] | SpecificationFileInfo[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState<"list" | "summary">("list");
  const [selectedSummary, setSelectedSummary] = useState<string>("");
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [deletingZip, setDeletingZip] = useState<string | null>(null);
  // 「取得に失敗した」と「登録が 0 件だった」を区別する。障害中に
  // 「見つかりません」と出すと未登録だと誤解される（#163）。
  const [loadError, setLoadError] = useState<string | null>(null);
  const { addNotification } = useNotification();

  const specZipGroups = useMemo((): SpecZipGroup[] => {
    if (type !== "specDocs" || !Array.isArray(fileData) || fileData.length === 0) {
      return [];
    }
    const specFiles = fileData as SpecificationFileInfo[];
    const byZip = new Map<string, SpecificationFileInfo[]>();
    for (const f of specFiles) {
      const key = f.source_zip_name ?? "";
      if (!byZip.has(key)) byZip.set(key, []);
      byZip.get(key)!.push(f);
    }
    return Array.from(byZip.entries()).map(([raw, files]) => {
      const first = files[0];
      // 同名ZIPの再アップロードを考慮し、upload_idごとに1回だけサイズ計上
      const seenUploads = new Map<string, number>();
      for (const file of files) {
        const uploadId = file.source_zip_upload_id || "";
        const size = file.zip_file_size ?? 0;
        if (!size) continue;
        if (uploadId) {
          if (!seenUploads.has(uploadId)) {
            seenUploads.set(uploadId, size);
          }
        } else {
          // 旧データ（upload_idなし）は従来互換で source_zip_name 単位
          if (!seenUploads.has(`legacy:${raw}`)) {
            seenUploads.set(`legacy:${raw}`, size);
          }
        }
      }
      const zipFileSize = Array.from(seenUploads.values()).reduce(
        (acc, val) => acc + val,
        0
      );
      return {
        source_zip_name: raw || "（既存の仕様書）",
        raw_source_zip_name: raw,
        uploader_name: first.uploader_name,
        upload_time: first.upload_time,
        file_count: files.length,
        zip_file_size: zipFileSize,
        files,
      };
    });
  }, [type, fileData]);

  const handleDeleteSpecZip = async (rawSourceZipName: string) => {
    if (!projectId || !rawSourceZipName) return;
    if (!window.confirm(`この仕様書（ZIP）を削除しますか？`)) return;
    setDeletingZip(rawSourceZipName);
    try {
      const res = await ProjectAPI.deleteSpecificationDocsByZip(projectId, rawSourceZipName);
      if (res.success) {
        addNotification({
          type: "success",
          title: "削除完了",
          message: "仕様書を削除しました。",
        });
        onSpecZipDeleted?.();
        await fetchFileInfo();
      } else {
        throw new Error(res.error);
      }
    } catch (e) {
      addNotification({
        type: "error",
        title: "削除失敗",
        message: e instanceof Error ? e.message : "仕様書の削除に失敗しました。",
      });
    } finally {
      setDeletingZip(null);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes <= 0) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const config = {
    projectRules: {
      title: "プロジェクトルールファイル情報",
      emptyMessage: "プロジェクトルールファイルが見つかりません",
      errorMessage: "プロジェクトルールファイル情報を取得できませんでした",
      fileNameKey: "file_name" as const,
    },
    specDocs: {
      title: "仕様書ファイル情報",
      emptyMessage: "仕様書ファイルが見つかりません",
      errorMessage: "仕様書ファイル情報を取得できませんでした",
      fileNameKey: "name" as const,
    },
  };

  const fetchFileInfo = async () => {
    if (!projectId) return;

    setIsLoading(true);
    setLoadError(null);
    // Clear previous data immediately to prevent showing stale data
    setFileData([]);
    
    try {
      const response =
        type === "projectRules"
          ? await ProjectAPI.getProjectRulesInfo(projectId)
          : await ProjectAPI.getSpecificationDocsInfo(projectId);

      console.log("File info response:", response);

      if (response.success && response.data) {
        // プロジェクトルールは exists フラグで有無を返すため専用処理
        if (type === "projectRules") {
          const info = response.data as any;
          // 未登録時は空配列として扱い、空メッセージを表示
          if (info.exists === false) {
            setFileData([]);
            onDataLoaded?.();
            return;
          }
          // 登録ありの場合は表示用にキーを正規化して配列化
          if (info.exists === true) {
            const normalized = [
              {
                file_name: info.file_name || "",
                uploader_name: info.uploaded_by || info.uploader_name || "",
                upload_time: info.uploaded_at || info.upload_time || "",
                last_modified_time:
                  info.last_modified_time || info.uploaded_at || "",
              },
            ];
            setFileData(normalized);
            onDataLoaded?.();
            return;
          }
        }

        // 仕様書など通常の配列/コレクション系レスポンス処理
        let dataArray = response.data;
        if (!Array.isArray(dataArray)) {
          if (dataArray.files && Array.isArray(dataArray.files)) {
            dataArray = dataArray.files;
          } else if (dataArray.data && Array.isArray(dataArray.data)) {
            dataArray = dataArray.data;
          } else {
            dataArray = [dataArray];
          }
        }
        console.log("Processed file data:", dataArray);
        setFileData(dataArray);
        onDataLoaded?.();
      } else {
        throw new Error(response.error || "ファイル情報の取得に失敗しました");
      }
    } catch (error) {
      console.error("File info fetch error:", error);
      const reason =
        error instanceof Error
          ? error.message
          : "ファイル情報の取得に失敗しました";
      addNotification({
        type: "error",
        title: "エラー",
        message: reason,
      });
      setFileData([]);
      setLoadError(reason);
      onDataLoaded?.();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && projectId) {
      fetchFileInfo();
    }
  }, [isOpen, projectId, type, refreshTrigger]);

  useEffect(() => {
    if (!isOpen) {
      setView("list");
      setSelectedSummary("");
      setSelectedFileName("");
      setFileData([]);
    }
  }, [isOpen]);

  const openSummaryView = (summary: string, fileName: string) => {
    setSelectedSummary(summary || "");
    setSelectedFileName(fileName || "");
    setView("summary");
  };

  const backToList = () => {
    setView("list");
    setSelectedSummary("");
    setSelectedFileName("");
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} maxWidth="1200px">
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {view === "list" ? config[type].title : "ファイルサマリ"}
            </ModalTitle>
          </ModalHeader>

          {isLoading ? (
            <div style={{ textAlign: "center", padding: "2rem" }}>
              読み込み中...
            </div>
          ) : view === "list" ? (
            type === "specDocs" && specZipGroups.length > 0 ? (
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <TableHeader>仕様書ZIP名</TableHeader>
                      <TableHeader>アップロード者</TableHeader>
                      <TableHeader>アップロード時刻</TableHeader>
                      <TableHeader>ファイル数</TableHeader>
                      <TableHeader>サイズ</TableHeader>
                      <TableHeader>操作</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {specZipGroups.map((group, index) => (
                      <tr key={index}>
                        <TableCell>{group.source_zip_name}</TableCell>
                        <TableCell>{group.uploader_name}</TableCell>
                        <TableCell>
                          {formatBackendDateTimeLocal(group.upload_time)}
                        </TableCell>
                        <TableCell>{group.file_count}</TableCell>
                        <TableCell>{formatBytes(group.zip_file_size)}</TableCell>
                        <TableCell>
                          {group.raw_source_zip_name ? (
                            <DeleteIconButton
                              type="button"
                              title="この仕様書（ZIP）を削除"
                              aria-label="この仕様書（ZIP）を削除"
                              onClick={() =>
                                handleDeleteSpecZip(group.raw_source_zip_name)
                              }
                              disabled={deletingZip === group.raw_source_zip_name}
                            >
                              🗑️
                            </DeleteIconButton>
                          ) : (
                            <span title="既存データは一括削除のみ可能">-</span>
                          )}
                        </TableCell>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>
            ) : loadError ? (
              <EmptyMessage>
                {`${config[type].errorMessage}: ${loadError}`}
              </EmptyMessage>
            ) : !Array.isArray(fileData) || fileData.length === 0 ? (
              <EmptyMessage>{config[type].emptyMessage}</EmptyMessage>
            ) : (
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <TableHeader>ファイル名</TableHeader>
                      <TableHeader>アップロード者</TableHeader>
                      <TableHeader>アップロード時刻</TableHeader>
                      <TableHeader>最終更新時刻</TableHeader>
                      {type === "specDocs" && (
                        <>
                          <TableHeader>チャンク数</TableHeader>
                          <TableHeader>サマリ</TableHeader>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(fileData) &&
                      fileData.map((file, index) => (
                        <tr key={index}>
                          <TableCell>
                            {type === "projectRules"
                              ? (file as ProjectRulesFileInfo).file_name
                              : (file as SpecificationFileInfo).name}
                            {type === "specDocs" &&
                              (file as SpecificationFileInfo).path && (
                                <div
                                  style={{
                                    fontSize: "0.875rem",
                                    color: "#6c757d",
                                  }}
                                >
                                  パス: {(file as SpecificationFileInfo).path}
                                </div>
                              )}
                          </TableCell>
                          <TableCell>{file.uploader_name}</TableCell>
                          <TableCell>
                            {formatBackendDateTimeLocal(file.upload_time)}
                          </TableCell>
                          <TableCell>
                            {formatBackendDateTimeLocal(file.last_modified_time)}
                          </TableCell>
                          {type === "specDocs" && (
                            <>
                              <TableCell>
                                {(file as SpecificationFileInfo).chunk_count ||
                                  0}
                              </TableCell>
                              <TableCell>
                                {(file as SpecificationFileInfo).summary ? (
                                  <SummaryButton
                                    onClick={() =>
                                      openSummaryView(
                                        (file as SpecificationFileInfo)
                                          .summary!,
                                        (file as SpecificationFileInfo).name
                                      )
                                    }
                                  >
                                    📄 サマリ表示
                                  </SummaryButton>
                                ) : (
                                  "サマリなし"
                                )}
                              </TableCell>
                            </>
                          )}
                        </tr>
                      ))) ||
                      null}
                  </tbody>
                </Table>
              </TableWrapper>
            )
          ) : (
            <div>
              <DetailHeader>
                <h3 style={{ margin: 0 }}>
                  ファイルサマリ: {selectedFileName}
                </h3>
                <BackButton onClick={backToList}>← 戻る</BackButton>
              </DetailHeader>
              <SummaryBody>
                {selectedSummary ? (
                  <ReactMarkdown>{selectedSummary}</ReactMarkdown>
                ) : (
                  <pre
                    style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}
                  >
                    サマリがありません
                  </pre>
                )}
              </SummaryBody>
            </div>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
